import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { driversApi } from "@/api/drivers.api";
import { queryKeys } from "@/utils/queryKeys";
import type { ListQuery } from "@/types/common";
import type { DriverStatus } from "@/types/domain";

export function useDriversList(query: ListQuery & { status?: DriverStatus; online?: boolean }) {
  return useQuery({
    queryKey: queryKeys.drivers.list(query),
    queryFn: () => driversApi.list(query),
    placeholderData: (prev) => prev,
  });
}

export function useDriver(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.drivers.detail(id ?? ""),
    queryFn: () => driversApi.getById(id as string),
    enabled: !!id,
  });
}

/** PATCH /admin/drivers/:id/status — one endpoint for every status transition. */
export function useSetDriverStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, reason }: { id: string; status: DriverStatus; reason?: string }) =>
      driversApi.setStatus(id, status, reason),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.drivers.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: ["drivers", "list"] });
    },
  });
}
